import { useState, useEffect, useCallback, useRef } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Mail, Loader2, Info } from 'lucide-react';
import { trackEvent, AnalyticsEvents } from '@/utils/analytics';
import { debugLog } from '@/lib/debugLog';
import { isAppleNativeRuntime } from '@/lib/runtimePlatform';
import { buildApiUrl } from '@/config/api';

const PKCE_VERIFIER_KEY = 'native_auth_pkce_verifier';
const PKCE_STATE_KEY = 'native_auth_pkce_state';

function setPKCEState(codeVerifier: string, state: string) {
  localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  localStorage.setItem(PKCE_STATE_KEY, state);
}



// Get Kinde configuration from environment
const runtimeEnv = window.__ENV;
const getKindeConfig = () => ({
  clientId: runtimeEnv?.VITE_KINDE_CLIENT_ID ?? import.meta.env.VITE_KINDE_CLIENT_ID ?? '',
  domain: runtimeEnv?.VITE_KINDE_DOMAIN ?? import.meta.env.VITE_KINDE_DOMAIN ?? '',
});

const getRegistrationSearchParams = () => new URLSearchParams(window.location.search);

const shouldStartRegistrationFlow = (isAppleNativeApp: boolean) => {
  if (isAppleNativeApp) {
    return false;
  }

  const searchParams = getRegistrationSearchParams();
  return window.location.pathname === '/register' || searchParams.get('register') === '1';
};

const getRegistrationEmailHint = () => getRegistrationSearchParams().get('email')?.trim() ?? '';

export default function Login() {
  const [error, setError] = useState('');
  const [registrationError, setRegistrationError] = useState('');
  const isAppleNativeApp = isAppleNativeRuntime();
  const [showRegistrationForm, setShowRegistrationForm] = useState(() => shouldStartRegistrationFlow(isAppleNativeApp));
  const [registrationForm, setRegistrationForm] = useState(() => ({
    firstName: '',
    lastName: '',
    email: getRegistrationEmailHint(),
  }));

  const [pendingInvitation, setPendingInvitation] = useState<string | null>(null);
  const [nativeLoading, setNativeLoading] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const browserListenerRef = useRef<{ remove: () => void } | null>(null);

  // Kinde auth hooks
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();

  // Clean up browser listener on unmount
  useEffect(() => {
    return () => {
      browserListenerRef.current?.remove();
    };
  }, []);

  /**
   * Generate PKCE code verifier and challenge for OAuth
   */
  const generatePKCE = useCallback(async () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const codeVerifier = btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    
    return { codeVerifier, codeChallenge };
  }, []);

  /**
   * Handle native OAuth login using in-app browser
   */
  const handleNativeLogin = useCallback(async (isRegister = false, loginHint?: string) => {
    debugLog('[Login] Starting native OAuth flow');
    setNativeLoading(true);
    setError('');
    
    // Clear previous debug logs for fresh debugging
    localStorage.removeItem('native_auth_debug');
    localStorage.removeItem('native_auth_last_error');
    
    try {
      const { clientId, domain } = getKindeConfig();
      
      if (!clientId || !domain) {
        throw new Error('Kinde configuration missing. Check VITE_KINDE_CLIENT_ID and VITE_KINDE_DOMAIN.');
      }

      // Generate PKCE challenge
      const { codeVerifier, codeChallenge } = await generatePKCE();
      
      // Build Kinde authorization URL
      const redirectUri = 'cleariest://auth/callback';
      const state = crypto.randomUUID();
      
      // Store PKCE state in localStorage (persists across app restart from deep link)
      setPKCEState(codeVerifier, state);
      debugLog('[Login] PKCE state stored');
      
      // Get audience from runtime env or build-time env
      const runtimeEnv = window.__ENV;
      const audience = runtimeEnv?.VITE_KINDE_AUDIENCE ?? 
        import.meta.env.VITE_KINDE_AUDIENCE ?? 
        runtimeEnv?.VITE_API_URL ?? 
        import.meta.env.VITE_API_URL;
      
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid profile email offline',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        // Show success page on Kinde before redirecting - helps with custom URL schemes
        has_success_page: 'true',
      });

      // Add audience if configured (required for proper JWT audience claim)
      if (audience) {
        params.append('audience', audience);
        debugLog('[Login] Added audience to auth URL:', audience);
      }

      if (loginHint) {
        params.append('login_hint', loginHint);
      }

      if (!isRegister && isAppleNativeApp) {
        params.append('prompt', 'login');
      }

      // For registration, add start_page parameter
      if (isRegister) {
        params.append('start_page', 'registration');
      }

      const authUrl = `${domain}/oauth2/auth?${params.toString()}`;
      debugLog('[Login] Opening auth URL:', authUrl);

      const { Browser } = await import('@capacitor/browser');
      
      // Open in external browser (not in-app WebView)
      await Browser.open({ 
        url: authUrl,
        presentationStyle: 'popover',
        windowName: '_blank'
      });
      
      debugLog('[Login] Browser opened successfully');

      // Listen for browser close to detect user cancellation.
      // If auth succeeds the app navigates away; if cancelled, reset loading.
      browserListenerRef.current?.remove();
      const listener = await Browser.addListener('browserFinished', () => {
        debugLog('[Login] Browser closed without completing auth, resetting loading state');
        setTimeout(() => {
          setNativeLoading(false);
        }, 1500);
        listener.remove();
        browserListenerRef.current = null;
      });
      browserListenerRef.current = listener;
    } catch (err) {
      console.error('[Login] Native OAuth error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open login. Please try again.');
      setNativeLoading(false);
    }
  }, [generatePKCE, isAppleNativeApp]);

  /**
   * Handle login - uses native flow on mobile, Kinde SDK on web
   */
  const handleLogin = useCallback((loginHint?: string) => {
    if (Capacitor.isNativePlatform()) {
      handleNativeLogin(false, loginHint);
    } else {
      kindeLogin(loginHint ? { loginHint } : undefined);
    }
  }, [handleNativeLogin, kindeLogin]);

  const handleRegistrationFieldChange = useCallback((field: 'firstName' | 'lastName' | 'email', value: string) => {
    setRegistrationForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleStartRegistration = useCallback(() => {
    if (isAppleNativeApp) {
      return;
    }

    setError('');
    setRegistrationError('');
    setShowRegistrationForm(true);
  }, [isAppleNativeApp]);

  const handleRegistrationSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const firstName = registrationForm.firstName.trim();
    const lastName = registrationForm.lastName.trim();
    const email = registrationForm.email.trim().toLowerCase();

    if (!firstName) {
      setRegistrationError('First name is required.');
      return;
    }

    if (!lastName) {
      setRegistrationError('Last name is required.');
      return;
    }

    if (!email) {
      setRegistrationError('Email is required.');
      return;
    }

    setError('');
    setRegistrationError('');
    setRegistrationLoading(true);

    if (!pendingInvitation) {
      sessionStorage.setItem('postAuthRedirect', '/workspaces/create');
    }

    trackEvent(AnalyticsEvents.SIGNUP_STARTED, {
      method: 'custom_kinde_registration',
      has_invitation: !!pendingInvitation,
      page: 'login'
    });

    try {
      const response = await fetch(buildApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
        }),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        setRegistrationError(body?.message ?? 'Failed to create account. Please try again.');
        return;
      }

      handleLogin(body?.email ?? email);
    } catch (submitError) {
      console.error('[Login] Registration request failed:', submitError);
      setRegistrationError('Failed to create account. Please try again.');
    } finally {
      setRegistrationLoading(false);
    }
  }, [handleLogin, pendingInvitation, registrationForm]);

  // Check for pending invitation token from InvitationAcceptPage
  useEffect(() => {
    const inviteToken = sessionStorage.getItem('pendingInvitationToken');
    if (inviteToken) {
      setPendingInvitation(inviteToken);
    }

    const emailHint = getRegistrationEmailHint();
    if (emailHint) {
      setRegistrationForm(prev => ({
        ...prev,
        email: prev.email || emailHint,
      }));
    }

    if (shouldStartRegistrationFlow(isAppleNativeApp)) {
      setShowRegistrationForm(true);
    }
    
    // Check for error in URL params (from native auth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get('error');
    const urlMsg = urlParams.get('msg');
    const urlDetails = urlParams.get('details');
    
    if (urlError) {
      const errorMessages: Record<string, string> = {
        'state_mismatch': 'Authentication state mismatch. Please try again.',
        'pkce_lost': 'Authentication session expired. Please try again.',
        'token_exchange_failed': 'Failed to complete authentication.',
        'exchange_error': 'An error occurred during authentication.',
        'missing_params': 'Authentication callback was incomplete. Please try again.',
        'auth_failed': 'Authentication failed. Please try again.',
        'config_missing': 'App configuration is missing. Please reinstall the app.',
      };
      
      let errorText = errorMessages[urlError] || `Authentication error: ${urlError}`;
      
      // Add details if available
      if (urlMsg) {
        errorText += ` (${urlMsg})`;
      }
      if (urlDetails) {
        errorText += ` Details: ${urlDetails}`;
      }
      
      // Check for debug logs
      const debugLogs = localStorage.getItem('native_auth_debug');
      const lastError = localStorage.getItem('native_auth_last_error');
      if (debugLogs || lastError) {
        console.log('[Login] Native auth debug logs:', debugLogs);
        console.log('[Login] Last error:', lastError);
        // Show debug info in UI
        let info = '';
        if (lastError) {
          info += `Last Error: ${lastError}\n\n`;
        }
        if (debugLogs) {
          try {
            const logs = JSON.parse(debugLogs);
            info += 'Debug Log:\n' + logs.map((l: { time: string; msg: string }) => `${l.time}: ${l.msg}`).join('\n');
          } catch {
            info += `Raw logs: ${debugLogs}`;
          }
        }
      }
      
      setError(errorText);
      // Clear the error from URL to avoid showing it on refresh
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const isBusy = kindeLoading || nativeLoading || registrationLoading;

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 pt-[max(1rem,var(--safe-area-top))] pb-[max(1rem,var(--safe-area-bottom))]">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/android-chrome-192x192.png" 
              alt="Cleariest" 
              className="h-16 w-16 rounded-xl"
            />
          </div>
          <CardTitle className="text-2xl font-bold">Welcome to Cleariest</CardTitle>
          <CardDescription className="text-foreground/80">
            Transparent Team Communication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingInvitation && (
            <Alert className="border-blue-200 bg-blue-50">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                {isAppleNativeApp
                  ? "You've been invited to join a workspace. Sign in with an existing account to accept in the app."
                  : "You've been invited to join a workspace. Sign in or create an account to accept."}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {registrationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{registrationError}</AlertDescription>
            </Alert>
          )}

          {isAppleNativeApp && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Account creation and workspace setup are not available in the iPhone and iPad app. Sign in with an existing account, or ask your workspace admin to invite this email first.
              </AlertDescription>
            </Alert>
          )}



          <div className="space-y-3">
            {!pendingInvitation && !isAppleNativeApp && !showRegistrationForm && (
              <div className="text-center mb-2">
                <p className="text-sm text-foreground/80">
                  New to Cleariest?
                </p>
              </div>
            )}

            {!isAppleNativeApp && showRegistrationForm && (
              <form className="space-y-3" onSubmit={handleRegistrationSubmit}>
                <div className="space-y-2">
                  <label htmlFor="registration-first-name" className="text-sm font-medium text-foreground">
                    First name
                  </label>
                  <Input
                    id="registration-first-name"
                    autoComplete="given-name"
                    value={registrationForm.firstName}
                    onChange={(event) => handleRegistrationFieldChange('firstName', event.target.value)}
                    disabled={isBusy}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="registration-last-name" className="text-sm font-medium text-foreground">
                    Last name
                  </label>
                  <Input
                    id="registration-last-name"
                    autoComplete="family-name"
                    value={registrationForm.lastName}
                    onChange={(event) => handleRegistrationFieldChange('lastName', event.target.value)}
                    disabled={isBusy}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="registration-email" className="text-sm font-medium text-foreground">
                    Email
                  </label>
                  <Input
                    id="registration-email"
                    type="email"
                    autoComplete="email"
                    value={registrationForm.email}
                    onChange={(event) => handleRegistrationFieldChange('email', event.target.value)}
                    disabled={isBusy}
                  />
                </div>

                <Button type="submit" className="w-full bg-foreground text-background hover:bg-foreground/90" disabled={isBusy}>
                  {isBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    pendingInvitation ? 'Create Account to Accept Invitation' : 'Create Account'
                  )}
                </Button>

                <p className="text-xs text-center text-foreground/80">
                  No password is stored here. After registration, you'll continue straight to Kinde for a one-time code.
                </p>

                <button
                  type="button"
                  className="mx-auto block text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                  disabled={isBusy}
                  onClick={() => {
                    setRegistrationError('');
                    handleLogin(registrationForm.email.trim().toLowerCase() || undefined);
                  }}
                >
                  Already have an account? Sign in
                </button>
              </form>
            )}

            {!isAppleNativeApp && !showRegistrationForm && (
              <>
                <Button
                  onClick={handleStartRegistration}
                  type="button"
                  className="w-full bg-foreground text-background hover:bg-foreground/90"
                  disabled={isBusy}
                >
                  {isBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>

                {!pendingInvitation && (
                  <p className="text-xs text-center text-foreground/80">
                    You'll set up your workspace after signing up
                  </p>
                )}

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-foreground/80">or</span>
                  </div>
                </div>

                <p className="text-xs text-center text-foreground/80">
                  Already have an account?
                </p>
              </>
            )}

            {!showRegistrationForm && (
              <Button onClick={() => handleLogin()} type="button" variant="outline" className="w-full" disabled={isBusy}>
              {pendingInvitation ? 'Sign In to Accept Invitation' : 'Sign In'}
              </Button>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          {!isAppleNativeApp && (
            <div className="w-full pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-medium text-foreground/80">Looking for more info?</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                  onClick={() => window.location.href = 'https://cleariest.com'}
                >
                  Visit our website →
                </Button>
              </div>
            </div>
          )}
          
          <p className="text-center text-xs text-foreground/80">
            By signing in, you agree to our <a className="underline" href="https://cleariest.com/terms.html">Terms of Service</a> and <a className="underline" href="https://cleariest.com/privacy.html">Privacy Policy</a> 
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
